/**
 * Resolve safe in-app path after login / select-studio.
 * Prefers React Router `location.state.from`, then `?return=` query.
 */
export const DEFAULT_POST_AUTH_PATH = "/dashboard";

const isSafeInternalPath = (value: string): boolean =>
  value.startsWith("/") && !value.startsWith("//") && !value.includes("://");

export type LocationLike = {
  pathname?: string;
  search?: string;
};

export const pathFromLocationLike = (from?: LocationLike | null): string | null => {
  if (!from?.pathname || !isSafeInternalPath(from.pathname)) {
    return null;
  }

  return `${from.pathname}${from.search || ""}`;
};

export const pathFromReturnQuery = (raw: string | null | undefined): string | null => {
  if (!raw) {
    return null;
  }

  try {
    const decoded = decodeURIComponent(raw).trim();

    if (!isSafeInternalPath(decoded)) {
      return null;
    }

    return decoded;
  } catch {
    return null;
  }
};

export const resolvePostAuthRedirect = (
  locationState: { from?: LocationLike } | null | undefined,
  returnQuery: string | null | undefined
): string =>
  pathFromLocationLike(locationState?.from) ||
  pathFromReturnQuery(returnQuery) ||
  DEFAULT_POST_AUTH_PATH;

export const buildLoginUrlWithReturn = (pathname: string, search = ""): string => {
  const path = `${pathname}${search}`;

  if (!isSafeInternalPath(path) || path === "/login" || path.startsWith("/login?")) {
    return "/login";
  }

  return `/login?return=${encodeURIComponent(path)}`;
};
